import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

/**
 * ErrorStateService - Gestione centralizzata degli errori di connessione
 * 
 * Responsabilità:
 * - Traccia lo stato di errore globale dell'applicazione
 * - Gestisce il conteggio dei retry falliti
 * - Notifica i componenti quando mostrare/nascondere l'overlay di errore
 * - Fornisce meccanismo di retry manuale
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorStateService {
  
  // Configurazione retry
  private readonly MAX_CONSECUTIVE_FAILURES = 3;
  private consecutiveFailures = 0;
  
  // Stato errore globale
  private _isInErrorState = new BehaviorSubject<boolean>(false);
  public isInErrorState$ = this._isInErrorState.asObservable();
  
  // Messaggio errore corrente
  private _errorMessage = new BehaviorSubject<string>('');
  public errorMessage$ = this._errorMessage.asObservable();
  
  // Tipo di errore (network, server, timeout, unknown)
  private _errorType = new BehaviorSubject<'network' | 'server' | 'timeout' | 'unknown'>('unknown');
  public errorType$ = this._errorType.asObservable();
  
  // Evento per trigger retry da parte dell'utente
  private _retryRequested = new Subject<void>();
  public retryRequested$ = this._retryRequested.asObservable();
  
  // Flag per evitare notifiche multiple
  private isProcessingError = false;

  constructor() {
    console.log('🔴 ErrorStateService initialized');
  }

  /**
   * Registra un fallimento di connessione
   * Dopo MAX_CONSECUTIVE_FAILURES, attiva lo stato di errore
   */
  registerFailure(errorType: 'network' | 'server' | 'timeout' | 'unknown', message: string): void {
    if (this.isProcessingError) return;
    
    this.consecutiveFailures++;
    console.log(`🔴 ErrorStateService: Failure registered (${this.consecutiveFailures}/${this.MAX_CONSECUTIVE_FAILURES}) - Type: ${errorType}`);
    
    if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
      this.activateErrorState(errorType, message);
    }
  }

  /**
   * Registra un successo - resetta il contatore dei fallimenti
   */
  registerSuccess(): void {
    if (this.consecutiveFailures > 0) {
      console.log('🟢 ErrorStateService: Success registered - resetting failure counter');
      this.consecutiveFailures = 0;
    }
    
    // Se eravamo in stato di errore, disattivalo
    if (this._isInErrorState.getValue()) {
      this.deactivateErrorState();
    }
  }

  /**
   * Attiva lo stato di errore globale
   */
  private activateErrorState(errorType: 'network' | 'server' | 'timeout' | 'unknown', message: string): void {
    if (this._isInErrorState.getValue()) return; // Già in errore
    
    this.isProcessingError = true;
    console.log(`🔴 ErrorStateService: Activating error state - ${errorType}: ${message}`);
    
    this._errorType.next(errorType);
    this._errorMessage.next(this.getLocalizedMessage(errorType, message));
    this._isInErrorState.next(true);
    
    this.isProcessingError = false;
  }

  /**
   * Disattiva lo stato di errore
   */
  private deactivateErrorState(): void {
    console.log('🟢 ErrorStateService: Deactivating error state');
    this._isInErrorState.next(false);
    this._errorMessage.next('');
    this._errorType.next('unknown');
    this.consecutiveFailures = 0;
  }

  /**
   * Richiede un retry manuale - chiamato dall'utente
   */
  requestRetry(): void {
    console.log('🔄 ErrorStateService: Retry requested by user');
    this.consecutiveFailures = 0;
    this._isInErrorState.next(false);
    this._retryRequested.next();
  }

  /**
   * Forza il reset dello stato di errore
   */
  forceReset(): void {
    console.log('🔄 ErrorStateService: Force reset');
    this.deactivateErrorState();
  }

  /**
   * Ottiene il messaggio localizzato in base al tipo di errore
   */
  private getLocalizedMessage(errorType: 'network' | 'server' | 'timeout' | 'unknown', originalMessage: string): string {
    switch (errorType) {
      case 'network':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'server':
        return 'The server is temporarily unavailable. Please try again later.';
      case 'timeout':
        return 'The request timed out. Please try again.';
      default:
        return originalMessage || 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Getter per lo stato corrente (sincrono)
   */
  get isInErrorState(): boolean {
    return this._isInErrorState.getValue();
  }

  /**
   * Getter per il numero di fallimenti consecutivi
   */
  get failureCount(): number {
    return this.consecutiveFailures;
  }
}
